/**
 * WebSocket protocol between the Bruno widget and the API.
 * Two-way JSON messages.
 *
 * SECURITY: every visitor-authored field has an explicit upper bound.
 * The widget itself caps DOM extraction in apps/widget/src/domExtract.ts,
 * but the server never trusts the widget — a hostile actor can write
 * their own client and send arbitrarily large payloads. These Zod caps
 * are the second line of defense; the WS frame size cap
 * (env WS_VISITOR_MAX_PAYLOAD_BYTES) is the first. Caps are sized to be
 * ~30-50% larger than what a healthy widget produces today, so future
 * widget updates don't paint themselves into a corner without explicitly
 * raising the server bound.
 */
import { z } from 'zod';
// ---------- Page context the widget sends ----------
/**
 * For `<select>` elements, the page extractor includes a compact list of the
 * available options so the LLM can pass the EXACT `value=` attribute as the
 * propose_action payload. Without this, the model only sees the visible
 * label text and ships e.g. `payload: "Modern"` for a `<option value="modern_dark">`,
 * which makes the select go blank because no option matches.
 */
export const SelectOption = z.object({
    value: z.string().max(500),
    label: z.string().max(200),
});
export const InteractiveElement = z.object({
    selector: z.string().max(500),
    tag: z.string().max(32),
    role: z.string().max(100).nullable(),
    name: z.string().max(500).nullable(),
    text: z.string().max(500).nullable(),
    type: z.string().max(100).nullable().optional(),
    // RFC 3986 doesn't define a hard URL length but most browsers cap around
    // 2048; we accept up to 4096 to leave room for query strings.
    href: z.string().max(4096).nullable().optional(),
    ariaLabel: z.string().max(500).nullable().optional(),
    visible: z.boolean(),
    // Only populated for <select> elements. Capped at 50 to keep the prompt
    // bounded; selects with more options are extremely rare in practice.
    options: z.array(SelectOption).max(50).optional(),
});
export const NavLink = z.object({
    href: z.string().max(4096),
    text: z.string().max(500),
});
/**
 * Informational content container the widget surfaces alongside interactive
 * elements. Lets `highlight_element` ring cards / sections / articles so
 * Bruno can answer "where can I read about X?" questions by scrolling and
 * pointing at the matching block. Never used by `propose_action`.
 */
export const InfoBlock = z.object({
    selector: z.string().max(500),
    tag: z.string().max(32),
    role: z.string().max(100).nullable(),
    heading: z.string().max(500).nullable(),
    excerpt: z.string().max(1000),
    textLength: z.number().int().nonnegative().max(1_000_000),
});
/**
 * Sane integer bound for a screen dimension, in CSS pixels. Anything bigger
 * is either an attempted overflow attack or someone's 16K monitor wall.
 */
const screenDim = z.number().int().nonnegative().max(100_000);
export const PageContext = z.object({
    url: z.string().max(4096),
    title: z.string().max(500),
    pathname: z.string().max(2048),
    scrollY: z.number().int().min(-10_000_000).max(10_000_000),
    viewport: z.object({ width: screenDim, height: screenDim }),
    outline: z
        .string()
        .max(8192)
        .describe('Compact textual outline of headings and landmarks'),
    elements: z.array(InteractiveElement).max(150),
    // Same-origin <a> links found on the page (visible OR hidden, e.g. mobile menus).
    // Lets the model build a mental map of available routes for navigate_hint.
    links: z.array(NavLink).max(80).default([]),
    // Informational content blocks (cards / sections / articles) the model can
    // ring with `highlight_element` to answer "where is X" questions. Defaults
    // to [] so older widget builds that don't ship this field still validate.
    infoBlocks: z.array(InfoBlock).max(60).default([]),
});
// ---------- Client (widget) -> Server messages ----------
export const ClientHello = z.object({
    type: z.literal('hello'),
    embedKey: z.string().uuid(),
    sessionId: z.string().uuid(),
    origin: z.string().max(2048),
    pageContext: PageContext,
});
export const ClientUserMessage = z.object({
    type: z.literal('user_message'),
    content: z.string().min(1).max(4000),
    pageContext: PageContext,
});
export const ClientToolResult = z.object({
    type: z.literal('tool_result'),
    // Tool-call ids are server-issued and short (uuid-ish); a malicious
    // client can't smuggle a megabyte of state via this field.
    toolCallId: z.string().min(1).max(128),
    status: z.enum(['ok', 'denied', 'error', 'not_found']),
    detail: z.string().max(2000).optional(),
});
export const ClientPageChange = z.object({
    type: z.literal('page_change'),
    pageContext: PageContext,
});
export const ClientNavigationStart = z.object({ type: z.literal('navigation_start') });
/**
 * Visitor-initiated request to mint a one-time cross-origin handoff token.
 * Sent right before Bruno navigates the visitor to ANOTHER origin on the same
 * registered site (e.g. acme.com -> app.acme.com under a *.acme.com
 * registration). sessionStorage is origin-scoped and does NOT carry across
 * that boundary, so the token lets the destination page rejoin the same
 * conversation. The server binds the token to (websiteId, conversationId,
 * sessionId, destinationOrigin); only the destinationOrigin and a continuation
 * hint are visitor-authored, hence the caps below. The continuation mirrors
 * the same-origin `pendingContinuation` so the next page can auto-resume the
 * multi-page task.
 */
export const ClientRequestHandoff = z.object({
    type: z.literal('request_handoff'),
    destinationOrigin: z.string().max(2048),
    continuation: z
        .object({
        originalRequest: z.string().max(4000),
        reason: z.string().max(2000),
        continuationCount: z.number().int().min(0).max(1000),
    })
        .optional(),
});
export const ClientActivity = z.object({
    type: z.literal('client_activity'),
    activity: z.enum(['panel_open', 'panel_close', 'panel_pointer', 'composer_input', 'composer_focus']),
});
export const ClientHeartbeat = z.object({ type: z.literal('heartbeat') });
/**
 * Visitor-initiated end-of-chat. The visitor clicks "End chat" in the
 * widget; the server marks the conversation as ended and broadcasts the
 * resolution to any connected agent. The optional reason mirrors what an
 * agent can capture when resolving from the inbox side, capped here so a
 * compromised widget can't ship a megabyte of "reason".
 */
export const ClientEndConversation = z.object({
    type: z.literal('end_conversation'),
    reason: z.string().max(500).optional(),
});
export const ClientMessage = z.discriminatedUnion('type', [
    ClientHello,
    ClientUserMessage,
    ClientToolResult,
    ClientPageChange,
    ClientNavigationStart,
    ClientRequestHandoff,
    ClientActivity,
    ClientHeartbeat,
    ClientEndConversation,
]);
// ---------- WS close codes ----------
export const WS_CLOSE = {
    NORMAL: 1000,
    POLICY_VIOLATION: 4000,
    UNAUTHORIZED: 4001,
    ORIGIN_FORBIDDEN: 4002,
    PLAN_AT_CAPACITY: 4003,
    WEBSITE_DISABLED: 4004,
    RATE_LIMITED: 4005,
    NAVIGATION_HANDOFF: 4006,
    FREE_QUOTA_EXHAUSTED: 4007,
};
//# sourceMappingURL=protocol.js.map